import { z } from 'zod';
import type { AreaBriefSchema, BriefMoveSchema } from '../schemas/areaBriefSchemas';
import { Timestamp as FirestoreTimestamp } from '@firebase/firestore';
import {
  stakeholderTypeSchema,
  postCategorySchema,
  checklistThreadAuthorRoleSchema,
  userRoleSchema,
  postVisibilitySchema,
  evaluationStatusSchema,
  LocationSchema,
  GeoJSONPolygonSchema,
  defenseStrategySchema,
  defenseFeatureKindSchema,
  planVisibilitySchema,
  PlanBoundsSchema,
  DefensePlanFeatureSchema,
  DefensePlanSchema,
  PlanBundleSchema,
  PlanEventSchema,
  PlanEventRsvpSchema,
  PlanEventLinkSchema,
  PlanEventMeetingPlaceSchema,
  planPartyTypeSchema,
  PlanPartySchema,
  PlanPartyContactSchema,
  PlanBidSchema,
  FundSchema,
  FundLedgerEntrySchema,
  FundingRequestSchema,
  planBidStatusSchema,
  planAssignmentStatusSchema,
  planAssignmentSourceSchema,
  DefensePlanAssignmentSchema,
  planAssignmentUpdateStatusSchema,
  planAssignmentUpdateSourceSchema,
  PlanAssignmentUpdateSchema,
  mapOverlayVisibilitySchema,
  MapOverlaySchema,
  fundingSourceKindSchema,
  fundingSourceStatusSchema,
  FundingSourceSchema,
  AreaContactSchema,
  PeopleListSchema,
  PersonSchema,
  vendorStatusSchema,
  vendorSourceSchema,
  VendorSchema,
  VendorRepSchema,
  VendorEditSchema,
  VendorEditChangesSchema,
  RepEditChangesSchema,
  VendorRatingSchema,
  PlanBundleInvitedVendorSchema,
  AgentProfileSchema,
  UserSchema,
  StructureSchema,
  StructureClaimSchema,
  ClaimSummarySchema,
  ProgressSnapshotSchema,
  PostSchema,
  CommentSchema,
  ChecklistItemSchema,
  ChecklistBrandSchema,
  InsuranceChecklistSchema,
  ChecklistThreadMessageSchema,
  ChecklistProgressSchema,
  ServiceAreaSchema,
  NotificationPreferencesSchema,
  NotificationSchema,
  DeviceSchema,
  notificationTypeSchema,
} from '../schemas/firestoreSchemas';

// Re-export Timestamp for convenience — consumers should not need to
// import from @firebase/firestore directly.
export type Timestamp = FirestoreTimestamp;

// ----- Domain types derived from Zod schemas (single source of truth) -----

export type StakeholderType = z.infer<typeof stakeholderTypeSchema>;
export type PostCategory = z.infer<typeof postCategorySchema>;
export type ChecklistThreadAuthorRole = z.infer<typeof checklistThreadAuthorRoleSchema>;
export type UserRole = z.infer<typeof userRoleSchema>;
export type PostVisibility = z.infer<typeof postVisibilitySchema>;
export type EvaluationStatus = z.infer<typeof evaluationStatusSchema>;

// Display labels + descriptions for the 9 roles. Phase 1 capability lives
// only for fire-scout / property-owner / insurance-agent — the rest render
// in UI as "label + Coming soon / Schedule a call".
export const USER_ROLE_META: Record<UserRole, { label: string; blurb: string; phase: 1 | 2 | 3 }> =
  {
    'fire-scout': {
      label: 'Fire Scout',
      blurb: 'Look for opportunities to prepare your community for fire.',
      phase: 1,
    },
    'property-owner': {
      label: 'Property Owner',
      blurb: 'Prepare your property, secure insurance, or lower premiums.',
      phase: 1,
    },
    'insurance-agent': {
      label: 'Insurance Agent',
      blurb: 'Help property owners secure insurance or lower premiums.',
      phase: 1,
    },
    'insurance-company': {
      label: 'Insurance Company',
      blurb: 'Lower risk in a community.',
      phase: 2,
    },
    contractor: {
      label: 'Contractor',
      blurb: 'Build to standards that reduce risk.',
      phase: 2,
    },
    evaluator: {
      label: 'Evaluator',
      blurb: 'Record observations related to risk models.',
      phase: 2,
    },
    'fire-official': {
      label: 'Fire Official',
      blurb: 'Oversee community wildfire preparation.',
      phase: 2,
    },
    'community-leader': {
      label: 'Community Leader',
      blurb: 'Organize a campaign to lower our risk of wildfire.',
      phase: 2,
    },
    educator: {
      label: 'Educator / Facilitator',
      blurb: 'Help a community prepare for wildfire.',
      phase: 2,
    },
  };

export type Location = z.infer<typeof LocationSchema>;
export type GeoJSONPolygon = z.infer<typeof GeoJSONPolygonSchema>;
export type DefenseStrategy = z.infer<typeof defenseStrategySchema>;
export type DefenseFeatureKind = z.infer<typeof defenseFeatureKindSchema>;
export type PlanVisibility = z.infer<typeof planVisibilitySchema>;
export type PlanBounds = z.infer<typeof PlanBoundsSchema>;
export type DefensePlanFeature = z.infer<typeof DefensePlanFeatureSchema>;
export type DefensePlan = z.infer<typeof DefensePlanSchema>;
export type PlanBundle = z.infer<typeof PlanBundleSchema>;
export type PlanBundleTeamMember = NonNullable<PlanBundle['team']>[number];
export type PlanEvent = z.infer<typeof PlanEventSchema>;
export type PlanEventRsvp = z.infer<typeof PlanEventRsvpSchema>;
export type PlanEventLink = z.infer<typeof PlanEventLinkSchema>;
export type PlanEventMeetingPlace = z.infer<typeof PlanEventMeetingPlaceSchema>;
export type PlanPartyType = z.infer<typeof planPartyTypeSchema>;
export type PlanParty = z.infer<typeof PlanPartySchema>;
export type PlanPartyContact = z.infer<typeof PlanPartyContactSchema>;
export type PlanBid = z.infer<typeof PlanBidSchema>;
export type Fund = z.infer<typeof FundSchema>;
export type FundLedgerEntry = z.infer<typeof FundLedgerEntrySchema>;
export type FundingRequest = z.infer<typeof FundingRequestSchema>;
export type PlanBidStatus = z.infer<typeof planBidStatusSchema>;
export type PlanAssignmentStatus = z.infer<typeof planAssignmentStatusSchema>;
export type PlanAssignmentSource = z.infer<typeof planAssignmentSourceSchema>;
export type DefensePlanAssignment = z.infer<typeof DefensePlanAssignmentSchema>;
export type PlanAssignmentUpdateStatus = z.infer<typeof planAssignmentUpdateStatusSchema>;
export type PlanAssignmentUpdateSource = z.infer<typeof planAssignmentUpdateSourceSchema>;
export type PlanAssignmentUpdate = z.infer<typeof PlanAssignmentUpdateSchema>;
export type MapOverlayVisibility = z.infer<typeof mapOverlayVisibilitySchema>;
export type MapOverlay = z.infer<typeof MapOverlaySchema>;
export type FundingSourceKind = z.infer<typeof fundingSourceKindSchema>;
export type FundingSourceStatus = z.infer<typeof fundingSourceStatusSchema>;
export type FundingSource = z.infer<typeof FundingSourceSchema>;
export type AreaContact = z.infer<typeof AreaContactSchema>;
export type AreaBrief = z.infer<typeof AreaBriefSchema>;
export type BriefMove = z.infer<typeof BriefMoveSchema>;
export type PeopleList = z.infer<typeof PeopleListSchema>;
export type Person = z.infer<typeof PersonSchema>;
export type VendorStatus = z.infer<typeof vendorStatusSchema>;
export type VendorSource = z.infer<typeof vendorSourceSchema>;
export type Vendor = z.infer<typeof VendorSchema>;
export type VendorRep = z.infer<typeof VendorRepSchema>;
export type VendorEdit = z.infer<typeof VendorEditSchema>;
export type VendorEditChanges = z.infer<typeof VendorEditChangesSchema>;
export type RepEditChanges = z.infer<typeof RepEditChangesSchema>;
export type VendorRating = z.infer<typeof VendorRatingSchema>;
export type PlanBundleInvitedVendor = z.infer<typeof PlanBundleInvitedVendorSchema>;
export type AgentProfile = z.infer<typeof AgentProfileSchema>;
export type User = z.infer<typeof UserSchema>;
export type Structure = z.infer<typeof StructureSchema>;
export type StructureClaim = z.infer<typeof StructureClaimSchema>;
export type ClaimSummary = z.infer<typeof ClaimSummarySchema>;
export type ProgressSnapshot = z.infer<typeof ProgressSnapshotSchema>;
export type StructureWithClaim = Structure & { claim: StructureClaim | null };
// The slice of claim state the map's pin layer consumes. Sourced from the
// per-area claimSummaries docs (function-maintained); a full StructureClaim
// is structurally assignable, which the optimistic claim path relies on.
export type MapClaimSummary = {
  claimedBy: string;
  progressSnapshot?: { completed: number; total: number };
};
export type Post = z.infer<typeof PostSchema>;
export type Comment = z.infer<typeof CommentSchema>;
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;
export type ChecklistBrand = z.infer<typeof ChecklistBrandSchema>;
export type InsuranceChecklist = z.infer<typeof InsuranceChecklistSchema>;
export type ChecklistThreadMessage = z.infer<typeof ChecklistThreadMessageSchema>;
export type ChecklistProgress = z.infer<typeof ChecklistProgressSchema>;
export type ServiceArea = z.infer<typeof ServiceAreaSchema>;
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;
export type AppNotification = z.infer<typeof NotificationSchema>;
export type Device = z.infer<typeof DeviceSchema>;
export type NotificationType = z.infer<typeof notificationTypeSchema>;

/**
 * Normalize legacy 'good'/'bad' category values (written by FirePrep-era code)
 * to the canonical 'progress'/'risk'/'news' enum. Safe to call on
 * already-normalized values — it's a no-op for all three.
 */
export function normalizePostCategory(raw: string | undefined): PostCategory {
  if (raw === 'risk' || raw === 'bad') return 'risk';
  if (raw === 'news') return 'news';
  return 'progress';
}

// ----- Types without Firestore schemas (client-only) -----

// The defense-plan draw tool's shape, one wider than a stored feature's
// `kind`: 'home' picks a house on the map (useDefensePlanDraw.web.ts turns
// that into a committed kind:'point' feature with a structureId — see
// DefensePlanFeatureSchema's comment). UI-only on purpose, so it's typed
// here rather than folded into defenseFeatureKindSchema's zod enum, which
// stays exactly what a stored feature's `kind` can be.
export type PlanDrawKind = DefenseFeatureKind | 'home';

export interface LocationInput {
  latitude: number;
  longitude: number;
  address?: string;
  // Radius of the GPS fix in metres. Absent when the user placed the pin by
  // hand or linked a structure — there's no device accuracy to report.
  accuracyM?: number | null;
}

export interface MapRegion {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
}

export interface MapBounds {
  northEast: {
    latitude: number;
    longitude: number;
  };
  southWest: {
    latitude: number;
    longitude: number;
  };
}
